import { useState } from "react";
import { Menu, Group, LoadingOverlay, Box } from "@mantine/core";
import { HiArrowDownTray, HiChevronDown } from "react-icons/hi2";
import { FaFilePdf, FaFileCsv, FaFileExcel } from "react-icons/fa";

import CustomButton from "../CustomButton/CustomButton";
import { CustomText } from "../CustomText/CustomText";

const ExportButton = ({
  exportConfig = {},
  customExportHandlers = {},
  label = "Export",
  size = "sm",
  variant = "light",
  color = "primary",
  leftSection,
  radius = "md",
  disabled = false,
  position = "bottom-end",
  shadow = "md",
  width = "auto",
  showLoadingOverlay = false,
  className,
  onExportStart,
  onExportComplete,
  ...props
}) => {
  const [loadingType, setLoadingType] = useState(null);

  // Default export types
  const defaultConfigs = {
    pdf: {
      enabled: true,
      label: "PDF",
      icon: FaFilePdf,
      color: "primary",
      disabled: false,
    },
    csv: {
      enabled: true,
      label: "CSV",
      icon: FaFileCsv,
      color: "green",
      disabled: false,
    },
    excel: {
      enabled: true,
      label: "Excel",
      icon: FaFileExcel,
      color: "orange",
      disabled: false,
    },
  };

  const mergedConfig = { ...defaultConfigs, ...exportConfig };
  const enabledExports = Object.entries(mergedConfig).filter(
    ([_, cfg]) => cfg.enabled
  );
  const isSingleExport = enabledExports.length === 1;

  const handleExport = async (type) => {
    const handler = customExportHandlers[type];
    if (!handler) return;

    setLoadingType(type);
    onExportStart?.(type);

    try {
      await handler();
    } finally {
      setLoadingType(null);
      onExportComplete?.(type);
    }
  };

  if (isSingleExport) {
    const [type, cfg] = enabledExports[0];
    const Icon = cfg.icon;
    return (
      <Box pos="relative">
        {showLoadingOverlay && loadingType && <LoadingOverlay visible />}
        <CustomButton
          onClick={() => handleExport(type)}
          loading={loadingType === type}
          disabled={disabled || cfg.disabled}
          leftSection={leftSection || <Icon size={16} />}
          size={size}
          variant="default"
          color={cfg.color || color}
          radius={radius}
          className={className}
          {...props}
        >
          {loadingType === type
            ? `Exporting ${cfg.label}...`
            : `${label} ${cfg.label}`}
        </CustomButton>
      </Box>
    );
  }

  return (
    <Box pos="relative">
      {showLoadingOverlay && loadingType && <LoadingOverlay visible />}
      <Menu
        position={position}
        shadow={shadow}
        width={width}
        radius={radius}
        disabled={disabled}
      >
        <Menu.Target>
          <CustomButton
            rightSection={<HiChevronDown size={16} />}
            leftSection={leftSection || <HiArrowDownTray size={16} />}
            loading={!!loadingType}
            disabled={disabled}
            size={size}
            variant={variant}
            color={color}
            radius={radius}
            className={className}
            {...props}
          >
            {loadingType
              ? `Exporting ${mergedConfig[loadingType].label}...`
              : label}
          </CustomButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Export Formats</Menu.Label>
          {enabledExports.map(([type, cfg]) => (
            <Menu.Item
              key={type}
              leftSection={<cfg.icon size={16} />}
              disabled={loadingType || cfg.disabled}
              onClick={() => handleExport(type)}
              color={cfg.color}
            >
              <Group justify="space-between" w="100%">
                <CustomText size="sm">{cfg.label}</CustomText>
                {loadingType === type && (
                  <CustomText size="xs" c="dimmed">
                    Exporting...
                  </CustomText>
                )}
              </Group>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
};

export default ExportButton;
